import { CalendarDays, FileText } from 'lucide-react';

import { Button } from '@/components/ui/catalyst/button';
import { cn } from '@/lib/utils';
import type { ProviderDirectoryV2Service } from '@/services/types/provider-directory-v2-types';

interface ActivitySectionProps {
  service: ProviderDirectoryV2Service;
  onConfigureRequestPlan?: () => void;
  className?: string;
}

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const ActivitySection = ({
  service,
  onConfigureRequestPlan,
  className,
}: ActivitySectionProps) => {
  const matterCount = service.linked_matter_count ?? 0;
  const lastUsedDate = formatDate(service.last_used_at);
  const hasRequestPlan =
    service.clinical_location?.has_active_request_plan ?? false;

  return (
    <div className={cn('', className)}>
      {/* Section title */}
      <h2 className="text-[13px] font-semibold uppercase tracking-[0.05em] text-zinc-500 mb-3">
        Activity
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Linked Matters card */}
        <div className="rounded-xl border border-border bg-background p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-info-light" />
              <span className="text-sm font-semibold text-info-dark">
                {matterCount > 0
                  ? `${matterCount} Linked Matter${matterCount !== 1 ? 's' : ''}`
                  : 'Linked Matters'}
              </span>
            </div>
            {matterCount > 0 && (
              <button
                type="button"
                className="text-xs font-medium text-primary hover:underline"
              >
                View all
              </button>
            )}
          </div>

          {matterCount === 0 && (
            <p className="text-sm font-medium text-info-light">
              No linked matters yet
            </p>
          )}

          {lastUsedDate && (
            <p className="mt-2 flex items-center gap-1 text-xs text-zinc-500">
              <CalendarDays size={12} />
              Last used {lastUsedDate}
            </p>
          )}
        </div>

        {/* Request Plan card */}
        <div className="rounded-xl border border-border bg-background p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-semibold text-info-dark">
              Request Plan
            </span>
          </div>

          {hasRequestPlan ? (
            <p className="text-sm font-medium text-success mb-2">
              Active request plan configured
            </p>
          ) : (
            <p className="text-sm font-medium text-info-light mb-2">
              No active request plan
            </p>
          )}

          {onConfigureRequestPlan && (
            <Button
              color="dark/white"
              className="mt-1"
              onClick={onConfigureRequestPlan}
            >
              {hasRequestPlan
                ? 'Edit request plan'
                : 'Configure request plan'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
